<script lang="ts">
    import Card from '@tile/components/card/Card.svelte';
    import ColorThumbs from '@tile/components/color-thumbs/ColorThumbs.svelte';
    import DealBadge from '@tile/components/deal-badge/DealBadge.svelte';
    import EekLabel from '@tile/components/eek/EekLabel.svelte';
    import JsonLd from '@tile/components/json-ld/JsonLd.svelte';
    import OttoUp from '@tile/components/otto-up/OttoUp.svelte';
    import ProductBrand from '@tile/components/product-brand/ProductBrand.svelte';
    import ProductComparisonCheckBox from '@tile/components/product-comparison/ProductComparisonCheckBox.svelte';
    import ProductHeader from '@tile/components/product-header/ProductHeader.svelte';
    import type { ImageAspectRatio } from '@tile/components/product-image/types.ts';
    import ProductLink from '@tile/components/product-link/ProductLink.svelte';
    import ProductTitle from '@tile/components/product-title/ProductTitle.svelte';
    import RadioEquipmentRegulation from '@tile/components/radio-equipment-regulation/RadioEquipmentRegulation.svelte';
    import SocialProofBadges from '@tile/components/social-proof-badges/SocialProofBadges.svelte';
    import Sponsored from '@tile/components/sponsored/Sponsored.svelte';
    import TopInfo from '@tile/components/top-info/TopInfo.svelte';
    import { injectWiring } from '@tilelist/application/wiring/TileListWiringContext.svelte.ts';
    import type { Columns } from '@tilelist/domain/composition/GalleryLayout.ts';
    import { shouldShowWishlistIcon } from '@tilelist/domain/feature-visibility/WishlistElement.ts';
    import AvailabilityDispatcher from '@tilelist/layouts/dispatchers/availability-dispatcher/AvailabilityDispatcher.svelte';
    import PriceDispatcher from '@tilelist/layouts/dispatchers/price-dispatcher/PriceDispatcher.svelte';
    import ProductImageDispatcher from '@tilelist/layouts/dispatchers/product-image-dispatcher/ProductImageDispatcher.svelte';
    import RatingDispatcher from '@tilelist/layouts/dispatchers/rating-dispatcher/RatingDispatcher.svelte';
    import WishlistIcon from '@tilelist/layouts/experiments/e3803/wishlist-icon/WishlistIcon.svelte';
    import type { TileLayoutProps } from '@tilelist/layouts/shared/TileLayoutProps.ts';
    import type { TileLayout } from '@tilelist/layouts/tile-layout/TileLayout.ts';

    type Props = TileLayoutProps & {
        layout: TileLayout;
        aspectRatio: ImageAspectRatio;
        columns?: Columns;
        showInstallments: boolean;
        showAvailability: boolean;
        showRating: boolean;
        emphasizeTopInfos?: boolean;
        bestVariationBenefit?: string;
    };

    let {
        layout,
        aspectRatio,
        columns,
        imageLoadingOptions,
        onRedirectToPdp,
        rule,
        colors,
        sponsored,
        funderLegalName,
        advertiserLegalName,
        element = $bindable(null),
        showInstallments,
        showAvailability,
        showRating,
        emphasizeTopInfos = false,
        bestVariationBenefit,
    }: Props = $props();

    const onex = injectWiring().onex;
    const forceIndex = $derived(onex.isToggleActive('REPTILE_1495_FORCE_INDEX'));
    const showWishlistIcon = $derived(shouldShowWishlistIcon(onex));
    const { variation, title: productTitle, link: rawLink, showMultipleSizes } = $derived(layout);
    const link = $derived(forceIndex ? { ...rawLink, index: true } : rawLink);

    /**
 *
 *
 */
    const hasColors = $derived(!!(colors && colors.length > 1));
</script>

<Card
    data-jarvis="reptile-tile-container"
    data-variation-id={variation.variationId}
    bind:element
    disableOverlayEffect
    fullHeight
    size="50"
>
    <!-- NOTE: This is used in the Card component and is passed through -->
    {#snippet primaryAction()}
        <ProductLink id={`link-${variation.variationId}`} {link} onClick={onRedirectToPdp} />
    {/snippet}

    <ProductComparisonCheckBox currentVariation={variation} />

    <div class="reptile-tile-layout__image">
        <ProductImageDispatcher
            fallbackImage={layout.primaryImage}
            variationId={variation.variationId}
            detailPageLink={variation.detailPageLink}
            {imageLoadingOptions}
            onClick={onRedirectToPdp}
            {aspectRatio}
            {columns}
        />
        <DealBadge deal={variation.deal} variationId={variation.variationId} {rule} />
        <SocialProofBadges socialProof={variation.socialProof} />
        {#if showWishlistIcon}
            <WishlistIcon variationId={variation.variationId} multipleSizes={variation.multipleSizes} />
        {/if}
    </div>

    <div class="reptile-tile-layout__content">
        <ProductHeader>
            <ProductBrand brand={productTitle.brand} />
            <ProductTitle name={productTitle.display} />
            <TopInfo
                infos={variation.topInfos}
                multipleSizes={showMultipleSizes && !!variation.multipleSizes}
                emphasized={emphasizeTopInfos}
            />
            <RadioEquipmentRegulation radioEquipmentRegulation={variation.radioEquipmentRegulation} />
            <RatingDispatcher customerReviews={variation.customerReviews} {showRating} />
        </ProductHeader>

        <EekLabel labels={variation.energyLabels} asRow />

        <PriceDispatcher
            variationId={variation.variationId}
            saleTags={variation.saleTags}
            price={variation.price}
            dealHighlight={variation.deal?.highlight}
            dealId={variation.deal?.dealId}
            {rule}
            {showInstallments}
            {bestVariationBenefit}
        />

        <div class="reptile-tile-layout__purchase-info">
            {#if (showAvailability && variation.availability?.detail) || variation.ottoUp}
                <div class="reptile-tile-layout__row">
                    <AvailabilityDispatcher availability={variation.availability} {showAvailability} />
                    <OttoUp show={variation.ottoUp} />
                </div>
            {/if}

            {#if hasColors || sponsored}
                <div class="reptile-tile-layout__row" class:reptile-tile-layout__row--end={!hasColors}>
                    <ColorThumbs {colors} />
                    <Sponsored {sponsored} {funderLegalName} {advertiserLegalName} />
                </div>
            {/if}
        </div>
    </div>
</Card>

<JsonLd {variation} />

<style lang="scss">
    @use '@otto-ec/design-tokens/tokens';
    @use '@shared/styles/mixins' as reptileMixins;

    .reptile-tile-layout {
        &__image {
            position: relative;
        }

        &__content,
        &__purchase-info {
            display: flex;
            flex-direction: column;
        }

        &__content {
            gap: tokens.$oc-semantic-spacing-absolute-100;
            padding: tokens.$oc-semantic-spacing-absolute-75;
        }

        &__purchase-info {
            gap: tokens.$oc-semantic-spacing-absolute-50;
        }

        &__row {
            @include reptileMixins.row-space-between;

            gap: tokens.$oc-semantic-spacing-absolute-50;
            min-height: tokens.$oc-semantic-spacing-absolute-150;

            /**
 *
 */
            &--end {
                justify-content: flex-end;
            }
        }
    }
</style>
